import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { QuoteForm } from "../components/QuoteForm";
import { SEO } from "../components/SEO";
import { Check, ArrowRight } from "lucide-react";
import { getServiceSchema } from "../utils/structuredData";

export const PergolaInstallation = () => {
  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="Deponti Pergola Installation | London & Kent | Hudson Knox"
        description="Expert Deponti pergola installation across South East England. Premium aluminium pergola systems from authorised stockist. Professional installation in London, Kent & surrounding areas."
        keywords="pergola installation, Deponti pergolas, aluminium pergolas, pergola installer London, pergola installer Kent, outdoor structures, garden pergolas"
        canonicalPath="/services/pergola-installation"
        structuredData={getServiceSchema(
          "Pergola Installation",
          "Professional installation of premium Deponti aluminium pergola systems",
          "https://hudsonknox.co.uk/services/pergola-installation"
        )}
      />
      {/* Hero */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url(https://images.unsplash.com/photo-1762715576863-a10e7c38b5ed)",
            filter: "brightness(0.4)"
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-light text-white mb-4">Deponti Pergola Installation</h1>
          <p className="text-xl text-neutral-200">Premium aluminium pergola systems across South East England</p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-light text-neutral-900 mb-6">Authorised Deponti Stockist & Installer</h2>
              <p className="text-lg text-neutral-600 leading-relaxed mb-6">
                As an authorised Deponti stockist, we supply and install the complete range of premium Dutch-engineered aluminium pergolas. 
                From the budget-friendly Nebbiolo to the luxury Pigato with laminated glass roof, each precision-engineered system features 
                durable powder-coated aluminium in RAL colours with minimal maintenance requirements.
              </p>
              <p className="text-lg text-neutral-600 leading-relaxed mb-6">
                <strong>Popular Models in Stock:</strong> Giallo Plus (glass roof veranda), Nebbiolo (polycarbonate roof), Pigato (luxury safety glass), 
                and Pinela Deluxe (motorised louvered roof). All systems include integrated drainage, optional LED lighting, and professional installation.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-neutral-900 text-white hover:bg-neutral-800">
                  Request Quote <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_luxury-pergolas/artifacts/4xysqz7b_image008.png"
                alt="Deponti glass roof pergola dining"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-light text-neutral-900 mb-12 text-center">Benefits of Deponti Aluminium Systems</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: "Aluminium Construction",
                description: "100% aluminium frames - durable, rust-proof and long-lasting"
              },
              {
                title: "Precision Engineering",
                description: "Modular components manufactured to exacting standards for perfect assembly"
              },
              {
                title: "Powder-Coated Finish",
                description: "Durable RAL colour finishes that won't fade, chip or require repainting"
              },
              {
                title: "Dutch Engineering",
                description: "Premium quality Deponti systems designed and manufactured in the Netherlands"
              },
              {
                title: "Weather Resistant",
                description: "Rust-proof aluminium handles UK rain, wind and UV without deterioration"
              },
              {
                title: "Professional Installation",
                description: "Expert installation of your Deponti system with full manufacturer support"
              }
            ].map((benefit, index) => (
              <Card key={index} className="border-neutral-200">
                <CardContent className="p-6">
                  <h3 className="text-xl font-medium text-neutral-900 mb-3">{benefit.title}</h3>
                  <p className="text-neutral-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-light text-neutral-900 mb-12 text-center">Installation Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Consultation", description: "Free site survey and design consultation at your property" },
              { step: "02", title: "Design", description: "Custom 3D design tailored to your space and requirements" },
              { step: "03", title: "Manufacturing", description: "Precision-engineered pergola built to exact specifications" },
              { step: "04", title: "Installation", description: "Professional installation by our expert team" }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="text-6xl font-light text-neutral-200 mb-4">{process.step}</div>
                <h3 className="text-xl font-medium text-neutral-900 mb-3">{process.title}</h3>
                <p className="text-neutral-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Guide */}
      <section className="py-20 bg-neutral-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-light mb-6 text-center">Deponti Product Range</h2>
          <p className="text-neutral-300 text-center mb-12 text-lg">
            Professional installation of Deponti aluminium systems. Prices vary by size and specification - contact us for exact quotes.
          </p>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Check className="h-6 w-6 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium">Nebbiolo - Budget-Friendly Range</p>
                <p className="text-neutral-400">Aluminium frame with polycarbonate roof, widths 4-6m, RAL9001/7024 colours, reinforced beams</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Check className="h-6 w-6 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium">Giallo Plus - Glass Roof Veranda</p>
                <p className="text-neutral-400">7m max span, safety glass or polycarbonate roofing, RAL9016/7024/9005 finishes, 5-year warranty</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Check className="h-6 w-6 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium">Pinela Deluxe - Motorised Louvered Roof</p>
                <p className="text-neutral-400">Electric retractable system, 0-120° rotation, weather sensors, smart home integration</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Check className="h-6 w-6 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium">Pigato - Luxury Model</p>
                <p className="text-neutral-400">Steel reinforcement, 44.2mm laminated safety glass roof, premium specification</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-neutral-950">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-light text-white mb-4">Get Your Free Quote</h2>
            <p className="text-lg text-neutral-400">Professional pergola installation across South East England</p>
          </div>
          <QuoteForm title="Request Pergola Quote" />
        </div>
      </section>
    </div>
  );
};
